import {Injectable} from '@angular/core';
import {UrlParamsService} from './impl/url-params.service';
import {ApplicationStateService} from '../../shared/services/application-state.service';
import {
  BusinessParamName,
  BusinessParamValue,
  TechnicalParamName,
  TechnicalParamValue
} from '../../shared/models/request-offer/request-offer.enum';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';
import {SalesChannel} from '../consts/sales-channel.enum';
import {ApiRequestKey} from '../../shared/enums/api-request-key.enum';
import {API_REQUESTS_PARAMS_CONFIG} from '../configs/api-requests-params-config';
import {BusinessParameter, TechnicalParameter} from '../../shared/models/request-offer/request-header.model';
import {configurationCode, isCarOne, isKIOSKPOLIS, isYANOSIK, randomAlphanumeric, salesChannel} from '../utils/utils';
import {ProcessService} from './process.service';
import {InsuranceCompanyCode} from '../../shared/models/insurance-company-code.enum';
import {InsuranceCoverageService} from './impl/section-services/insurance-coverage/insurance-coverage.service';
import {PostCalculationContext} from './impl/calculation-policy.service';
import {ApplicationContextService} from './impl/configuration/application-context.service';
import {PartnerService} from '../../shared/services/partner.service';
import {AppCookieService} from '../../shared/services/app-cookie.service';
import {GoogleAnalyticsEventsService} from '../../shared/services/google-analytics-events.service';
import {SelectedOfferService} from './impl/section-services/selected-offer/selected-offer.service';
import {QueryParamName} from '../enums/query-param-name.enum';
import {OfferUtils} from '../../shared/utils/offer-utils';
import {LanguageService} from '../../shared/services/language.service';

@Injectable({
  providedIn: 'root'
})
export class ApiParamsService {

  private apiRequestsParamsConfig: ApiRequestParamsConfig[];
  private readonly CALCULATION_SIMPLE_ID_LENGTH = 8;
  private readonly PARAM_VALUES_CONFIG: ApiRequestParamValueConfig[] = [
      // business params
    {
      paramName: BusinessParamName.ANALYTICS_CLIENT_ID,
      getValue: this.getAnalyticsClientId
    },
    {
      paramName: BusinessParamName.ANALYTICS_SESSION_ID,
      getValue: this.getAnalyticsSessionId
    },
    {
      paramName: BusinessParamName.ANALYTICS_USER_ID,
      getValue: this.getAnalyticsUserId
    },
    {
      paramName: BusinessParamName.APPLICATION_CONTEXT,
      getValue: this.getApplicationContext
    },
    {
      paramName: BusinessParamName.APPLICATION_ID,
      getValue: this.getApplicationId
    },
    {
      paramName: BusinessParamName.CACHE_OFFER,
      getValue: this.getCacheOffer
    },
    {
      paramName: BusinessParamName.CALCULATION_ID,
      getValue: this.getCalculationId
    },
    {
      paramName: BusinessParamName.CALCULATION_SIMPLE_ID,
      getValue: this.getCalculationSimpleId
    },
    {
      paramName: BusinessParamName.CALCULATION_SOURCE,
      getValue: this.getCalculationSource
    },
    {
      paramName: BusinessParamName.CLIENT_SOURCE,
      getValue: this.getClientSource
    },
    {
      paramName: BusinessParamName.CONFIGURATION_CODE,
      getValue: this.getConfigurationCode
    },
    {
      paramName: BusinessParamName.CONTEXT,
      getValue: this.getContext
    },
    {
      paramName: BusinessParamName.DEVICE_SERIAL_NUMBER,
      getValue: this.getDeviceSerialNumber
    },
    {
      paramName: BusinessParamName.INSURANCE_COMPANY_CODE,
      getValue: this.getInsuranceCompanyCode
    },
    {
      paramName: BusinessParamName.INVALID_PARTNER_NAME,
      getValue: this.getInvalidPartnerName
    },
    {
      paramName: BusinessParamName.LOGGED_USER_ID,
      getValue: this.getLoggedUserId
    },
    {
      paramName: BusinessParamName.MAIL_NOTIFICATION,
      getValue: this.getMailNotification
    },
    {
      paramName: BusinessParamName.OFFER_ID,
      getValue: this.getOfferId
    },
    {
      paramName: BusinessParamName.OFFER_PACKAGE_NAME,
      getValue: this.getOfferPackageName
    },
    {
      paramName: BusinessParamName.OFFER_PRIMARY_COVERAGES,
      getValue: this.getOfferPrimaryCoverages
    },
    {
      paramName: BusinessParamName.ORDER_ID,
      getValue: this.getOrderId
    },
    {
      paramName: BusinessParamName.ORIGIN,
      getValue: this.getOrigin
    },
    {
      paramName: BusinessParamName.PARTNER_CALCULATION_ID,
      getValue: this.getPartnerCalculationId
    },
    {
      paramName: BusinessParamName.PARTNER_EXTERNAL_ID,
      getValue: this.getPartnerExternalId
    },
    {
      paramName: BusinessParamName.PARTNER_SHORT_NAME,
      getValue: this.getPartnerShortName
    },
    {
      paramName: BusinessParamName.PORTAL_PARTNER_ID,
      getValue: this.getPortalPartnerId
    },
    {
      paramName: BusinessParamName.PORTAL_USER_ID,
      getValue: this.getPortalUserId
    },
    {
      paramName: BusinessParamName.QUOTATION_ID,
      getValue: this.getQuotationId
    },
    {
      paramName: BusinessParamName.RECALCULATION_OFFER,
      getValue: this.getRecalculationOffer
    },
    {
      paramName: BusinessParamName.REFERENCE_OFFER_ID,
      getValue: this.getReferenceOfferId
    },
    {
      paramName: BusinessParamName.SALES_CHANNEL,
      getValue: this.getSalesChannel
    },
    {
      paramName: BusinessParamName.SELECTED_USER_FILTERS,
      getValue: this.getSelectedUserFilters
    },
    {
      paramName: BusinessParamName.SESSION_ID,
      getValue: this.getSessionId
    },
    {
      paramName: BusinessParamName.SESSION_RECREATE_ID,
      getValue: this.getSessionRecreateId
    },
    {
      paramName: BusinessParamName.SESSION_SIMPLE_ID,
      getValue: this.getSessionSimpleId
    },
    {
      paramName: BusinessParamName.SESSION_STAGE,
      getValue: this.getSessionStage
    },
    {
      paramName: BusinessParamName.STORAGE_CALCULATION_DATA_IN_PORTAL,
      getValue: this.getStorageCalculationDataInPortal
    },
      // technical params
    {
      paramName: TechnicalParamName.CALLBACK_NOTIFICATION,
      getValue: this.getCallbackNotification
    },
    {
      paramName: TechnicalParamName.DESTINATION,
      getValue: this.getDestination
    },
    {
      paramName: TechnicalParamName.OPERATION_NAME,
      getValue: this.getOperationName
    },
  ]

  constructor(private urlParamsService: UrlParamsService,
              private stateService: ApplicationStateService,
              private processService: ProcessService,
              private insuranceCoverageService: InsuranceCoverageService,
              private contextService: ApplicationContextService,
              private partnerService: PartnerService,
              private cookieService: AppCookieService,
              private googleAnalyticsService: GoogleAnalyticsEventsService,
              private selectedOfferService: SelectedOfferService,
              private languageService: LanguageService) {
    this.apiRequestsParamsConfig = API_REQUESTS_PARAMS_CONFIG;
  }

  createBusinessParams(data: ApiRequestParamData): BusinessParameter[] {
    const config = this.apiRequestsParamsConfig.find(item => item.requestKey === data.requestKey);

    if (isNotNullOrUndefined(config) && isNotNullOrUndefined(config.businessParams)) {
      const params: BusinessParameter[] = config.businessParams.map(paramName => ({
        parameterName: paramName,
        parameterValue: this.getParamValue(paramName, data)
      }));
      return params.filter(param => isNotNullOrUndefined(param.parameterValue));
    }
    return [];
  }

  createTechnicalParams(data: ApiRequestParamData): TechnicalParameter[] {
    const config = this.apiRequestsParamsConfig.find(item => item.requestKey === data.requestKey);

    if (isNotNullOrUndefined(config) && isNotNullOrUndefined(config.technicalParams)) {
      const params: TechnicalParameter[] = config.technicalParams.map(paramName => ({
        parameterName: paramName,
        parameterValue: this.getParamValue(paramName, data)
      }));
      return params.filter(param => isNotNullOrUndefined(param.parameterValue));
    }
    return [];
  }

  private getParamValue(paramName: BusinessParamName | TechnicalParamName, data: ApiRequestParamData): any {
    const paramValueConfig = this.PARAM_VALUES_CONFIG.find(config => config.paramName === paramName);
    if (isNotNullOrUndefined(paramValueConfig)) {
      return paramValueConfig.getValue.bind(this)(data);
    }
    return null;
  }

  // business params
  private getApplicationId(data: ApiRequestParamData): string {
    let applicationId;

    if (data.requestKey === ApiRequestKey.NOTIFICATION) {
      applicationId = this.stateService.getBusinessParameterValue(BusinessParamName.APPLICATION_ID);
    } else {
      applicationId = this.urlParamsService.getQueryParam(QueryParamName.APPLICATION_ID);
      if (!isNotNullOrUndefined(applicationId) && this.stateService.salesChannel === SalesChannel.BANKIER) {
        applicationId = this.stateService.state.bankierIdSalesChannelBankier;
      }
    }

    return applicationId;
  }

  private getConfigurationCode(): string {
    return configurationCode();
  }

  private getSessionId(): string {
    return this.processService.sessionId;
  }

  private getSessionSimpleId(): string {
    return this.processService.sessionSimpleId;
  }

  private getCalculationId(): string {
    return <string>this.stateService.getBusinessParameterValue(BusinessParamName.CALCULATION_ID);
  }

  private getStorageCalculationDataInPortal(): boolean {
    return true;
  }

  private getSalesChannel(): string {
    return salesChannel();
  }

  private getCacheOffer(): boolean {
    return <boolean>this.stateService.getBusinessParameterValue(BusinessParamName.CACHE_OFFER);
  }

  private getMailNotification(data: ApiRequestParamData): boolean {
    switch (data.requestKey) {
      case ApiRequestKey.OFFER_MODIFICATIONS:
        return true;
      case ApiRequestKey.POST_REGISTRATION:
        return true;
      case ApiRequestKey.PREPARE_REGISTRATION:
        return !isYANOSIK();
      case ApiRequestKey.POST_CALCULATION:
        return (isYANOSIK() || isCarOne()) ? false : !this.stateService.isSessionStageRecreate;
      default:
        return false;
    }
  }

  private getRecalculationOffer(): boolean {
    return false;
  }

  private getCalculationSource(): string {
    return BusinessParamValue.WWW;
  }

  private getCalculationSimpleId(data: ApiRequestParamData): string {
    switch (data.requestKey) {
      case ApiRequestKey.PREPARE_REGISTRATION:
        return <string>this.stateService.getBusinessParameterValue(BusinessParamName.CALCULATION_SIMPLE_ID);
      case ApiRequestKey.OFFER_MODIFICATIONS:
        return <string>this.stateService.getBusinessParameterValue(BusinessParamName.CALCULATION_SIMPLE_ID);
      case ApiRequestKey.POST_CALCULATION:
        let calculationSimpleId = <string>this.stateService.getBusinessParameterValue(BusinessParamName.CALCULATION_SIMPLE_ID);
        if (!data.payload.postCalculationContextDefined) {
          calculationSimpleId = randomAlphanumeric(this.CALCULATION_SIMPLE_ID_LENGTH).toUpperCase();
          this.stateService.saveBusinessParameter(BusinessParamName.CALCULATION_SIMPLE_ID, calculationSimpleId);
        }
        return calculationSimpleId;
      case ApiRequestKey.POST_REGISTRATION:
        return this.stateService.selectedOffer ? this.stateService.selectedOffer.simpleId : null;
      default:
        return null;
    }
  }

  private getSessionRecreateId(): string {
    return <string>this.stateService.getBusinessParameterValue(BusinessParamName.SESSION_RECREATE_ID);
  }

  private getSessionStage(): string {
    return <string>this.stateService.getBusinessParameterValue(BusinessParamName.SESSION_STAGE);
  }

  private getSelectedUserFilters(): string[] {
    return this.insuranceCoverageService.insuranceCoveragesSelectionEnabled ? this.insuranceCoverageService.selectedInsuranceCoverages : null;
  }

  private getLoggedUserId(): any {
    return this.urlParamsService.getQueryParam(QueryParamName.LOGGED_USER_ID);
  }

  private getOrigin(): string {
    return this.urlParamsService.getQueryParam(QueryParamName.ORIGIN);
  }

  private getPartnerExternalId(): string {
    let partnerExternalId = this.urlParamsService.getQueryParam(QueryParamName.PARTNER_EXTERNAL_ID);
    if (!isNotNullOrUndefined(partnerExternalId)) {
      partnerExternalId = this.stateService.getBusinessParameterValue(BusinessParamName.PARTNER_EXTERNAL_ID);
    }
    return partnerExternalId;
  }

  private getClientSource(): string {
    let clientSource = <string>this.stateService.getBusinessParameterValue(BusinessParamName.CLIENT_SOURCE); // client source determined in code, not from query param
    if (!isNotNullOrUndefined(clientSource)) {
      clientSource = this.urlParamsService.getQueryParam(QueryParamName.SOURCE);
    }
    return clientSource;
  }

  private getPartnerCalculationId(): string {
    return <string>this.stateService.getBusinessParameterValue(BusinessParamName.PARTNER_CALCULATION_ID);
  }

  private getContext(data: ApiRequestParamData): string {
    return (data && data.payload) ? data.payload.postCalculationContext : null;
  }

  private getAnalyticsUserId(): string {
    return this.stateService.analyticsUserId;
  }

  private getApplicationContext(data: ApiRequestParamData): string {
    if (data.requestKey === ApiRequestKey.NOTIFICATION) {
      return <string>this.stateService.getBusinessParameterValue(BusinessParamName.APPLICATION_CONTEXT);
    }
    return this.contextService.context;
  }

  private getDeviceSerialNumber(): string {
    return this.urlParamsService.getQueryParam(QueryParamName.DEVICE_SERIAL_NUMBER);
  }

  private getPartnerShortName(): string {
    const partnerData = this.partnerService.getPartnerDataFromLS();
    if (isNotNullOrUndefined(partnerData) && isNotNullOrUndefined(partnerData.partnerName)) {
      return partnerData.partnerName;
    }

    let partner = <string>this.stateService.getBusinessParameterValue(BusinessParamName.PARTNER_SHORT_NAME);
    if (!isNotNullOrUndefined(partner) && !isKIOSKPOLIS()) {
      partner = salesChannel();
    }

    return partner;
  }

  private getPortalPartnerId(): string {
    const portalPartnerData = this.partnerService.getPortalPartnerDataFromLS();
    if (isNotNullOrUndefined(portalPartnerData) && isNotNullOrUndefined(portalPartnerData.partnerId)) {
      return portalPartnerData.partnerId;
    }

    let portalPartnerId = <string>this.stateService.getBusinessParameterValue(BusinessParamName.PORTAL_PARTNER_ID);

    if (!isNotNullOrUndefined(portalPartnerId) && !isKIOSKPOLIS()) {
      portalPartnerId = salesChannel();
    }
    return portalPartnerId;
  }

  private getPortalUserId(): string {
    const portalPartnerData = this.partnerService.getPortalPartnerDataFromLS();
    if (isNotNullOrUndefined(portalPartnerData) && isNotNullOrUndefined(portalPartnerData.userId)) {
      return portalPartnerData.userId;
    }
    return <string>this.stateService.getBusinessParameterValue(BusinessParamName.PORTAL_USER_ID);
  }

  private getOfferId(data: ApiRequestParamData): string {
    if (isNotNullOrUndefined(data) && isNotNullOrUndefined(data.payload)) {
      return data.payload.offerId;
    }
    return null;
  }

  private getInsuranceCompanyCode(): string {
    return <string>this.stateService.getBusinessParameterValue(BusinessParamName.INSURANCE_COMPANY_CODE);
  }

  private getOrderId(): string {
    return <string>this.stateService.getBusinessParameterValue(BusinessParamName.ORDER_ID);
  }

  private getReferenceOfferId(): string {
    let referenceOfferId = this.urlParamsService.getQueryParam(QueryParamName.OFFER_ID);
    if (!isNotNullOrUndefined(referenceOfferId)) {
      referenceOfferId = this.stateService.getBusinessParameterValue(BusinessParamName.REFERENCE_OFFER_ID);
    }
    return referenceOfferId;
  }

  private getQuotationId(): string {
    return <string>this.stateService.getBusinessParameterValue(BusinessParamName.QUOTATION_ID);
  }

  private getAnalyticsClientId(): string {
    const gaCookieValue = this.cookieService.get(this.googleAnalyticsService.GA_COOKIE_KEY, false);
    return this.googleAnalyticsService.getClientId(gaCookieValue);
  }

  private getAnalyticsSessionId(): string {
    const gaContainerIdCookieValue = this.cookieService.get(this.googleAnalyticsService.containerIdCookieKey, false);
    return this.googleAnalyticsService.getSessionId(gaContainerIdCookieValue);
  }

  private getOfferPackageName(): string {
    return this.selectedOfferService.selectedOffer ? this.selectedOfferService.selectedOffer.packageName : null;
  }

  private getOfferPrimaryCoverages(): string[] {
    if (isNotNullOrUndefined(this.selectedOfferService.selectedOffer)) {
      return OfferUtils.getSelectedPrimaryCoveragesLabels(this.selectedOfferService.selectedOffer, this.languageService.getDefaultLanguage());
    }
    return [];
  }

  // technical params
  private getOperationName(data: ApiRequestParamData): string {
    switch (data.requestKey) {
      case ApiRequestKey.POST_CALCULATION:
        return TechnicalParamValue.CALCULATION;
      case ApiRequestKey.PREPARE_REGISTRATION:
        return `${TechnicalParamValue.PREPARE_REGISTRATION}_${this.stateService.insuranceCompanyCode}`;
      case ApiRequestKey.OFFER_MODIFICATIONS:
        switch (this.stateService.insuranceCompanyCode) {
          case InsuranceCompanyCode.AXA_DIRECT:
            return TechnicalParamValue.MODIFY_OFFER_AXA_DIRECT;
          case InsuranceCompanyCode.UNIQA:
            return TechnicalParamValue.MODIFY_OFFER_UNIQA;
          case InsuranceCompanyCode.PEVNO:
            return TechnicalParamValue.MODIFY_OFFER_UNIQA;
          default:
            return null;
        }
      case ApiRequestKey.POST_REGISTRATION:
        return TechnicalParamValue.SUBMIT_ORDER_POLICY;
    }
  }

  private getCallbackNotification(): string {
    return TechnicalParamValue.WEB_SOCKET;
  }

  private getDestination(): string {
    return TechnicalParamValue.BPS;
  }

  private getInvalidPartnerName(): string {
    if (isNotNullOrUndefined(this.partnerService.invalidPartnerName)) {
      return this.partnerService.invalidPartnerName;
    }
    return <string>this.stateService.getBusinessParameterValue(BusinessParamName.INVALID_PARTNER_NAME);
  }
}

export interface ApiRequestParamsConfig {
  requestKey: ApiRequestKey;
  businessParams?: BusinessParamName[];
  technicalParams?: TechnicalParamName[];
}

export interface ApiRequestParamValueConfig {
  paramName: BusinessParamName | TechnicalParamName;
  getValue: (data?: ApiRequestParamData) => any;
}

export interface ApiRequestParamData {
  requestKey: ApiRequestKey;
  payload?: {
    postCalculationContextDefined?: boolean;
    postCalculationContext?: PostCalculationContext;
    offerId?: string;
  };
}
